/* eslint-disable @angular-eslint/runtime-localize */
export const refreshInterval = 1000 * 10; // 10 seconds

export const getLanguageLabel = (languageCode?: string): string => {
  switch (languageCode?.toUpperCase()) {
    case "AR":
      return $localize`:@@config_language_ar:Arabe`;
    case "DE":
      return $localize`:@@config_language_de:Allemand`;
    case "EN":
      return $localize`:@@config_language_en:Anglais`;
    case "ES":
      return $localize`:@@config_language_es:Espagnol`;
    case "FR":
      return $localize`:@@config_language_fr:Français`;
    case "IT":
      return $localize`:@@config_language_it:Italien`;
    case "JA":
      return $localize`:@@config_language_ja:Japonais`;
    case "PT":
      return $localize`:@@config_language_pt:Portugais`;
    case "RM":
      return $localize`:@@config_language_rm:Romanche`;
    case "RU":
      return $localize`:@@config_language_ru:Russe`;
    case "ZH":
      return $localize`:@@config_language_zh:Chinois`;
    default:
      return "";
  }
};

export const config = Object.freeze({
  baseWikidataUrl: "https://www.wikidata.org/wiki/",
  typeformUrl: "https://form.typeform.com/to/C7vhgybs",
  typeformExpirationDate: "2024-12-31",
  guidelinePopUpExpirationDate: "2050-12-31",
});

export const tails = Object.freeze({
  NONE: Object.freeze({
    label: $localize`:@@config_tails_none:Non assigné`,
  }),
  SHORT_TAIL: Object.freeze({
    label: $localize`:@@config_tails_short:Court`,
  }),
  LONG_TAIL: Object.freeze({
    label: $localize`:@@config_tails_long:Long`,
  }),
});

export type Tail = keyof typeof tails;

export interface AudienceChartDataElement {
  key: string;
  label: string;
  hidden: boolean;
  normalizer: number;
}

export interface AudienceChartData {
  instagram: readonly Readonly<AudienceChartDataElement>[];
  articles: readonly Readonly<AudienceChartDataElement>[];
}

export const audienceChartData: Readonly<AudienceChartData> = Object.freeze({
  instagram: Object.freeze([
    Object.freeze({
      key: "numberOfPosts",
      label: $localize`:@@config_chart_instagram_numberofpost:Nombre de posts`,
      hidden: false,
      normalizer: 1,
    }),
    Object.freeze({
      key: "engagement",
      label: $localize`:@@config_chart_instagram_engagement:Engagement (en milliers)`,
      hidden: false,
      normalizer: 1000,
    }),
    Object.freeze({
      key: "impressions",
      label: $localize`:@@config_chart_instagram_impressions:Impressions`,
      hidden: true,
      normalizer: 1,
    }),
    Object.freeze({
      key: "saved",
      label: $localize`:@@config_chart_instagram_saved:Enregistré`,
      hidden: true,
      normalizer: 1,
    }),
    Object.freeze({
      key: "videoViews",
      label: $localize`:@@config_chart_instagram_videoviews:Vues de video (en milliers)`,
      hidden: true,
      normalizer: 1000,
    }),
    Object.freeze({
      key: "likes",
      label: $localize`:@@config_chart_instagram_likes:Likes (en milliers)`,
      hidden: true,
      normalizer: 1000,
    }),
    Object.freeze({
      key: "interactionRate",
      label: $localize`:@@config_chart_instagram_interactionRate:Taux d'interaction`,
      hidden: true,
      normalizer: 1,
    }),
    Object.freeze({
      key: "reach",
      label: $localize`:@@config_chart_instagram_reach:Portée (en milliers)`,
      hidden: true,
      normalizer: 1000,
    }),
    Object.freeze({
      key: "reelsAverageWatchTime",
      label: $localize`:@@config_chart_instagram_reelsAverageWatchTime:Durée de visionnage moyenne (en secondes)`,
      hidden: true,
      normalizer: 1000,
    }),
    Object.freeze({
      key: "shares",
      label: $localize`:@@config_chart_instagram_shares:Partages (en milliers)`,
      hidden: true,
      normalizer: 1000,
    }),
  ]),

  articles: Object.freeze([
    Object.freeze({
      key: "views",
      label: $localize`:@@config_chart_articles_views:Vues (total, en milliers)`,
      hidden: false,
      normalizer: 1000,
    }),
    Object.freeze({
      key: "appsViews",
      label: $localize`:@@config_chart_articles_appsviews:Vues (apps, en milliers)`,
      hidden: true,
      normalizer: 1000,
    }),
    Object.freeze({
      key: "siteViews",
      label: $localize`:@@config_chart_articles_siteviews:Vues (site, en milliers)`,
      hidden: true,
      normalizer: 1000,
    }),
    Object.freeze({
      key: "numberOfPosts",
      label: $localize`:@@config_chart_articles_numberofposts:Nombre d'articles`,
      hidden: false,
      normalizer: 1,
    }),
  ]),
});

export interface TaggingStatusDataValue {
  text: string;
  cssClass: string;
}

export interface TaggingStatusData {
  ERROR: Readonly<TaggingStatusDataValue>;
  TO_BE_TAGGED: Readonly<TaggingStatusDataValue>;
  TAGS_VALIDATED: Readonly<TaggingStatusDataValue>;
  UNAVAILABLE: Readonly<TaggingStatusDataValue>;
  AUTO_TAGGED: Readonly<TaggingStatusDataValue>;
  DOWNLOADING: Readonly<TaggingStatusDataValue>;
  TAGGING: Readonly<TaggingStatusDataValue>;
  TRANSCRIBING: Readonly<TaggingStatusDataValue>;
  NO_STATUS: Readonly<TaggingStatusDataValue>;
}

export type TaggingStatus = keyof TaggingStatusData;

export const taggingStatusData: Readonly<TaggingStatusData> = Object.freeze({
  ERROR: Object.freeze({ text: $localize`:@@config_taggin_status_error:Erreur`, cssClass: "p-button-danger" }),
  TO_BE_TAGGED: Object.freeze({
    text: $localize`:@@config_taggin_status_tobetagged:Analyser`,
    cssClass: "p-button-warning",
  }),
  TAGS_VALIDATED: Object.freeze({
    text: $localize`:@@config_taggin_status_validated:Validé`,
    cssClass: "p-button-success",
  }),
  UNAVAILABLE: Object.freeze({
    text: $localize`:@@config_taggin_status_unavailable:Audio indisponible`,
    cssClass: "p-button-outlined p-button-secondary",
  }),
  AUTO_TAGGED: Object.freeze({ text: $localize`:@@config_taggin_status_tagged:Analysé`, cssClass: "p-button-info" }),
  DOWNLOADING: Object.freeze({
    text: $localize`:@@config_taggin_status_downloading:Téléchargement`,
    cssClass: "p-button-secondary",
  }),
  TAGGING: Object.freeze({ text: $localize`:@@config_taggin_status_tagging:Taggage`, cssClass: "p-button-secondary" }),
  TRANSCRIBING: Object.freeze({
    text: $localize`:@@config_taggin_status_transcribing:Transcription`,
    cssClass: "p-button-secondary",
  }),
  NO_STATUS: Object.freeze({
    text: $localize`:@@config_taggin_status_none:Aucun statut`,
    cssClass: "p-button-secondary",
  }),
});

export interface UserNeedDataValue {
  label: string;
  description: string;
  icon: string;
}

export interface UserNeedData {
  HELP_ME: Readonly<UserNeedDataValue>;
  CONNECT_ME: Readonly<UserNeedDataValue>;
  UPDATE: Readonly<UserNeedDataValue>;
  TREND: Readonly<UserNeedDataValue>;
  ANALYSIS: Readonly<UserNeedDataValue>;
  EDUCATE: Readonly<UserNeedDataValue>;
  INSPIRE: Readonly<UserNeedDataValue>;
  DIVERT: Readonly<UserNeedDataValue>;
  NONE: Readonly<UserNeedDataValue>;
}

export type UserNeed = keyof UserNeedData;

export const userNeedData: Readonly<UserNeedData> = Object.freeze({
  HELP_ME: Object.freeze({
    label: $localize`:@@config_user_need_label_help_me:Aidez-moi`,
    description: $localize`:@@config_user_need_description_help_me:[ACTION] Aide moi dans la vie de tous les jours.`,
    icon: "fa fa-info",
  }),
  CONNECT_ME: Object.freeze({
    label: $localize`:@@config_user_need_label_connect_me:Connectez-moi`,
    description: $localize`:@@config_user_need_description_connect_me:[ACTION] Je veux me sentir connecté.`,
    icon: "fa fa-plug",
  }),
  UPDATE: Object.freeze({
    label: $localize`:@@config_user_need_label_update:Mettez-moi à jour`,
    description: $localize`:@@config_user_need_description_update:[FONCTIONNEL] Un reportage ordinaire, ce qu'il s'est passé`,
    icon: "fa fa-refresh",
  }),
  TREND: Object.freeze({
    label: $localize`:@@config_user_need_label_trend:Gardez-moi dans l'air du temps`,
    description: $localize`:@@config_user_need_description_trend:[FONCTIONNEL] Que disent les gens de cet événement sur les médias sociaux? En résumé, permettez-moi de comprendre l'esprit du temps.`,
    icon: "fa fa-line-chart",
  }),
  ANALYSIS: Object.freeze({
    label: $localize`:@@config_user_need_label_analysis:Donnez-moi de l'analyse`,
    description: $localize`:@@config_user_need_description_analysis:[CONTEXTUEL] Une analyse de l’actualité, de la façon dont un événement s’est déroulé ou de ce qu’il signifie.`,
    icon: "fa fa-eye",
  }),
  EDUCATE: Object.freeze({
    label: $localize`:@@config_user_need_label_educate:Expliquez-moi`,
    description: $localize`:@@config_user_need_description_educate:[CONTEXTUEL] Dites-m'en plus sur un sujet ou une personne dans l’histoire.`,
    icon: "fa fa-graduation-cap",
  }),
  INSPIRE: Object.freeze({
    label: $localize`:@@config_user_need_label_inspire:Inspirez-moi`,
    description: $localize`:@@config_user_need_description_inspire:[EMOTIONNEL] Présentez-moi une personne qui a surmonté des obstacles et me rend fier.`,
    icon: "fa fa-lightbulb-o",
  }),
  DIVERT: Object.freeze({
    label: $localize`:@@config_user_need_label_divert:Divertissez-moi`,
    description: $localize`:@@config_user_need_description_divert:[EMOTIONNEL] Montrez-moi quelque chose de plus léger, de moins sérieux en rapport avec cette histoire.`,
    icon: "fa fa-thumbs-o-up",
  }),
  NONE: Object.freeze({
    label: $localize`:@@config_user_need_label_none:Non assigné`,
    description: $localize`:@@config_user_need_description_none:Non assigné`,
    icon: "",
  }),
});

export const audioExtensions = Object.freeze([".m4a", ".mp3", ".wav"].flatMap((ext) => [ext, ext.toUpperCase()]));
export const videoExtensions = Object.freeze(
  [".mp4", ".webm", ".avi", ".mov"].flatMap((ext) => [ext, ext.toUpperCase()]),
);
export const fileContentExtensions = Object.freeze(
  [".pdf", ".docx", ".txt", ".srt", ".vtt", ".csv", ".xls", ".xlsx", ".ods", ".json", ".xml", ".html"].flatMap(
    (ext) => [ext, ext.toUpperCase()],
  ),
);

export const translationExtensions = Object.freeze(
  [".srt", ".vtt", ".txt", ".ttml", ".stl", ".xml"].flatMap((ext) => [ext, ext.toUpperCase()]),
);

export interface ArticleLengthDataValue {
  label: string;
  description: string;
  min: number;
  max: number;
}

export interface ArticleLengthData {
  LENGTH_0_999: Readonly<ArticleLengthDataValue>;
  LENGTH_1000_1999: Readonly<ArticleLengthDataValue>;
  LENGTH_2000_2999: Readonly<ArticleLengthDataValue>;
  LENGTH_3000_4999: Readonly<ArticleLengthDataValue>;
  LENGTH_5000_9999: Readonly<ArticleLengthDataValue>;
  LENGTH_10000: Readonly<ArticleLengthDataValue>;
}

export type ArticleLength = keyof typeof articleLengths;

export const articleLengths: Readonly<ArticleLengthData> = Object.freeze({
  LENGTH_0_999: Object.freeze({
    label: $localize`:@@config_length_0_999_label:moins de 1 minute`,
    description: $localize`:@@config_length_0_999_description:moins de 1 minute (1000 signes)`,
    min: 0,
    max: 999,
  }),
  LENGTH_1000_1999: Object.freeze({
    label: $localize`:@@config_length_1000_1999_label:entre 1 et 2 minutes`,
    description: $localize`:@@config_length_1000_1999_description:entre 1 et 2 minutes (1000 à 2000 signes)`,
    min: 1000,
    max: 1999,
  }),
  LENGTH_2000_2999: Object.freeze({
    label: $localize`:@@config_length_2000_2999_label:entre 2 et 3 minutes`,
    description: $localize`:@@config_length_2000_2999_description:entre 2 et 3 minutes (2000 à 3000 signes)`,
    min: 2000,
    max: 2999,
  }),
  LENGTH_3000_4999: Object.freeze({
    label: $localize`:@@config_length_3000_4999_label:entre 3 et 5 minutes`,
    description: $localize`:@@config_length_3000_4999_description:entre 3 et 5 minutes (3000 à 5000 signes)`,
    min: 3000,
    max: 4999,
  }),
  LENGTH_5000_9999: Object.freeze({
    label: $localize`:@@config_length_5000_9999_label:entre 5 et 10 minutes`,
    description: $localize`:@@config_length_5000_9999_description:entre 5 et 10 minutes (5000 à 10000 signes)`,
    min: 5000,
    max: 9999,
  }),
  LENGTH_10000: Object.freeze({
    label: $localize`:@@config_length_10000_label:plus de 10 minutes`,
    description: $localize`:@@config_length_10000_description:plus de 10 minutes (10000 signes)`,
    min: 10000,
    max: 99999999,
  }),
});

export interface FeatureTrackingDataValue {
  label: string;
}

export interface FeatureTrackingData {
  ARTICLE_GET: Readonly<FeatureTrackingDataValue>;
  ARTICLE_SUMMARY_SET: Readonly<FeatureTrackingDataValue>;
  ARTICLE_TAIL_SET: Readonly<FeatureTrackingDataValue>;
  ARTICLE_TAG_ADD: Readonly<FeatureTrackingDataValue>;
  ARTICLE_TAG_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  ARTICLE_ENTITY_ADD: Readonly<FeatureTrackingDataValue>;
  ARTICLE_ENTITY_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  ARTICLE_USERNEED_SET: Readonly<FeatureTrackingDataValue>;
  ARTICLE_SEARCH: Readonly<FeatureTrackingDataValue>;
  ARTICLE_STATS_LENGTH: Readonly<FeatureTrackingDataValue>;
  ARTICLE_STATS_USERNEED: Readonly<FeatureTrackingDataValue>;
  ARTICLE_STATS_ENTITY: Readonly<FeatureTrackingDataValue>;

  AUDIO_SEARCH: Readonly<FeatureTrackingDataValue>;
  AUDIO_GET: Readonly<FeatureTrackingDataValue>;
  AUDIO_TRANSLATE: Readonly<FeatureTrackingDataValue>;
  AUDIO_UPLOAD: Readonly<FeatureTrackingDataValue>;
  AUDIO_TEXT_EDIT: Readonly<FeatureTrackingDataValue>;
  AUDIO_SPEAKER_SET: Readonly<FeatureTrackingDataValue>;
  AUDIO_SPEAKER_DELETE: Readonly<FeatureTrackingDataValue>;
  AUDIO_DELETE: Readonly<FeatureTrackingDataValue>;
  AUDIO_SUMMARY_SET: Readonly<FeatureTrackingDataValue>;
  AUDIO_SUBTITLES_GENERATE: Readonly<FeatureTrackingDataValue>;

  DOCUMENT_UPLOAD: Readonly<FeatureTrackingDataValue>;
  DOCUMENT_GET: Readonly<FeatureTrackingDataValue>;
  DOCUMENT_DELETE: Readonly<FeatureTrackingDataValue>;
  DOCUMENT_CHAT: Readonly<FeatureTrackingDataValue>;
  DOCUMENT_CHAT_STREAM: Readonly<FeatureTrackingDataValue>;
  DOCUMENT_RESUME: Readonly<FeatureTrackingDataValue>;
  DOCUMENT_CHAT_SAVE: Readonly<FeatureTrackingDataValue>;

  ENTITY_SEARCH: Readonly<FeatureTrackingDataValue>;
  ENTITY_MERGE: Readonly<FeatureTrackingDataValue>;
  ENTITY_CREATE_LABEL: Readonly<FeatureTrackingDataValue>;
  ENTITY_CREATE_WIKIDATAID: Readonly<FeatureTrackingDataValue>;
  ENTITY_UPDATE: Readonly<FeatureTrackingDataValue>;

  INSTAGRAM_TAG_ADD: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_ENTITY_ADD: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_TAG_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_ENTITY_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_GET: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_SEARCH: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_STATS_ENGAGEMENT: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_STATS_ENTITY: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_TAIL_SET: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_USERNEED_SET: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_STATS_USERNEED: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_SUBTITLES_GENERATE: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_SPEAKER_SET: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_SPEAKER_DELETE: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_TEXT_EDIT: Readonly<FeatureTrackingDataValue>;
  INSTAGRAM_TRANSLATE: Readonly<FeatureTrackingDataValue>;

  PROMPT_USER_SAVE: Readonly<FeatureTrackingDataValue>;
  PROMPT_USER_DELETE: Readonly<FeatureTrackingDataValue>;

  AUDIO_PODCAST_SEARCH: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_TAG_ADD: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_ENTITY_ADD: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_TAG_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_ENTITY_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_GET: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_CLASSIFY: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_SUMMARY_SET: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_SPEAKER_SET: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_SPEAKER_DELETE: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_TAIL_SET: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_TEXT_EDIT: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_USERNEED_SET: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_TRANSLATE: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_SUBTITLES_GENERATE: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_STATS_USERNEED: Readonly<FeatureTrackingDataValue>;
  AUDIO_PODCAST_STATS_ENTITY: Readonly<FeatureTrackingDataValue>;

  PLAY_RTS_SEARCH: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_TAG_ADD: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_ENTITY_ADD: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_TAG_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_ENTITY_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_GET: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_CLASSIFY: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_SUMMARY_SET: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_SPEAKER_SET: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_SPEAKER_DELETE: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_TAIL_SET: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_TEXT_EDIT: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_USERNEED_SET: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_TRANSLATE: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_SUBTITLES_GENERATE: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_STATS_USERNEED: Readonly<FeatureTrackingDataValue>;
  PLAY_RTS_STATS_ENTITY: Readonly<FeatureTrackingDataValue>;

  AUDIOVISUAL_MEDIA_SEARCH: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_TAG_ADD: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_ENTITY_ADD: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_TAG_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_ENTITY_FEEDBACK: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_GET: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_SUMMARY_SET: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_SPEAKER_SET: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_SPEAKER_DELETE: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_TAIL_SET: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_TEXT_EDIT: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_USERNEED_SET: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_TRANSLATE: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_SUBTITLES_GENERATE: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_STATS_USERNEED: Readonly<FeatureTrackingDataValue>;
  AUDIOVISUAL_MEDIA_STATS_ENTITY: Readonly<FeatureTrackingDataValue>;
  API_AUDIOVISUAL_MEDIA_ANALYSIS: Readonly<FeatureTrackingDataValue>;

  USER_PROFILE_EDIT: Readonly<FeatureTrackingDataValue>;
  HOMEPAGE_FEATURE_DEMO_PLAY: Readonly<FeatureTrackingDataValue>;
  TOPBAR_ASSISTANT_HELP: Readonly<FeatureTrackingDataValue>;

  SUBTITLES_TRANSLATE: Readonly<FeatureTrackingDataValue>;

  VERTEXAI_STREAM: Readonly<FeatureTrackingDataValue>;

  SEARCH: Readonly<FeatureTrackingDataValue>;

  API_ARTICLE_ANALYSIS: Readonly<FeatureTrackingDataValue>;

  API_ENTITY_GET_ID: Readonly<FeatureTrackingDataValue>;
  API_ENTITY_GET_WIKIDATAID: Readonly<FeatureTrackingDataValue>;
  API_ENTITY_SEARCH: Readonly<FeatureTrackingDataValue>;
  API_ENTITY_CREATE_WIKIDATAID: Readonly<FeatureTrackingDataValue>;
  API_ENTITY_CREATE_LABEL: Readonly<FeatureTrackingDataValue>;

  API_PROMPT_DOPESHEET: Readonly<FeatureTrackingDataValue>;
  API_PROMPT_BY_ID: Readonly<FeatureTrackingDataValue>;
  API_PROMPT_CUSTOM: Readonly<FeatureTrackingDataValue>;
}

export const featureTrackings: Readonly<FeatureTrackingData> = Object.freeze({
  LOGIN: Object.freeze({ label: "Login sur Bakerstreet" }),

  ARTICLE_GET: Object.freeze({ label: "Article: affichage" }),
  ARTICLE_SUMMARY_SET: Object.freeze({ label: "Article: génération d'un résumé" }),
  ARTICLE_TAIL_SET: Object.freeze({ label: "Article: changement du tail" }),
  ARTICLE_TAG_ADD: Object.freeze({ label: "Article: ajout d'un tag IPTC" }),
  ARTICLE_TAG_FEEDBACK: Object.freeze({ label: "Article: feedback d'un tag IPTC" }),
  ARTICLE_ENTITY_ADD: Object.freeze({ label: "Article: ajout d'une entité" }),
  ARTICLE_ENTITY_FEEDBACK: Object.freeze({ label: "Article: feedback d'une entité" }),
  ARTICLE_USERNEED_SET: Object.freeze({ label: "Article: changement de userneed" }),
  ARTICLE_SEARCH: Object.freeze({ label: "Article: recherche" }),
  ARTICLE_STATS_LENGTH: Object.freeze({ label: "Article: statistiques du temps de lecture" }),
  ARTICLE_STATS_USERNEED: Object.freeze({ label: "Article: statistiques des userneeds" }),
  ARTICLE_STATS_ENTITY: Object.freeze({ label: "Article: statistiques des entités" }),

  AUDIO_SEARCH: Object.freeze({ label: "Audio & Vidéo: recherche" }),
  AUDIO_GET: Object.freeze({ label: "Audio & Vidéo: affichage" }),
  AUDIO_TRANSLATE: Object.freeze({ label: "Audio & Vidéo: traduction" }),
  AUDIO_UPLOAD: Object.freeze({ label: "Audio & Vidéo: upload" }),
  AUDIO_TEXT_EDIT: Object.freeze({ label: "Audio & Vidéo: édition de texte" }),
  AUDIO_SPEAKER_SET: Object.freeze({ label: "Audio & Vidéo: changement de speaker" }),
  AUDIO_SPEAKER_DELETE: Object.freeze({ label: "Audio & Vidéo: suppression de speaker" }),
  AUDIO_DELETE: Object.freeze({ label: "Audio & Vidéo: suppression" }),
  AUDIO_SUMMARY_SET: Object.freeze({ label: "Audio & Vidéo: génération d'un résumé" }),
  AUDIO_SUBTITLES_GENERATE: Object.freeze({ label: "Audio & Vidéo: génération de sous-titres" }),

  DOCUMENT_UPLOAD: Object.freeze({ label: "Document: upload" }),
  DOCUMENT_GET: Object.freeze({ label: "Document: affichage" }),
  DOCUMENT_DELETE: Object.freeze({ label: "Document: suppression" }),
  DOCUMENT_CHAT: Object.freeze({ label: "Document: tchat" }),
  DOCUMENT_CHAT_STREAM: Object.freeze({ label: "Document: streaming tchat" }),
  DOCUMENT_RESUME: Object.freeze({ label: "Document: reprise du tchat" }),
  DOCUMENT_CHAT_SAVE: Object.freeze({ label: "Document: sauvegarde du tchat" }),

  ENTITY_SEARCH: Object.freeze({ label: "Entité: recherche" }),
  ENTITY_MERGE: Object.freeze({ label: "Entité: fusion" }),
  ENTITY_CREATE_LABEL: Object.freeze({ label: "Entité: création par label" }),
  ENTITY_CREATE_WIKIDATAID: Object.freeze({ label: "Entité: création par wikidata ID" }),
  ENTITY_UPDATE: Object.freeze({ label: "Entité: mise à jour" }),

  INSTAGRAM_TAG_ADD: Object.freeze({ label: "Instagram: ajout d'un tag IPTC" }),
  INSTAGRAM_ENTITY_ADD: Object.freeze({ label: "Instagram: ajout d'une entité" }),
  INSTAGRAM_TAG_FEEDBACK: Object.freeze({ label: "Instagram: feedback d'un tag IPTC" }),
  INSTAGRAM_ENTITY_FEEDBACK: Object.freeze({ label: "Instagram: feedback d'une entité" }),
  INSTAGRAM_GET: Object.freeze({ label: "Instagram: affichage" }),
  INSTAGRAM_SEARCH: Object.freeze({ label: "Instagram: recherche" }),
  INSTAGRAM_STATS_ENGAGEMENT: Object.freeze({ label: "Instagram: statistiques d'engagement" }),
  INSTAGRAM_STATS_ENTITY: Object.freeze({ label: "Instagram: statistiques des entités" }),
  INSTAGRAM_TAIL_SET: Object.freeze({ label: "Instagram: changement du tail" }),
  INSTAGRAM_USERNEED_SET: Object.freeze({ label: "Instagram: changement de userneed" }),
  INSTAGRAM_STATS_USERNEED: Object.freeze({ label: "Instagram: statistiques des userneeds" }),
  INSTAGRAM_SUBTITLES_GENERATE: Object.freeze({ label: "Instagram: génération de sous-titres" }),
  INSTAGRAM_SPEAKER_SET: Object.freeze({ label: "Instagram: changement de speaker" }),
  INSTAGRAM_SPEAKER_DELETE: Object.freeze({ label: "Instagram: suppression de speaker" }),
  INSTAGRAM_TEXT_EDIT: Object.freeze({ label: "Instagram: édition du texte" }),
  INSTAGRAM_TRANSLATE: Object.freeze({ label: "Instagram: traduction" }),

  PROMPT_USER_SAVE: Object.freeze({ label: "Prompt: sauvegarde" }),
  PROMPT_USER_DELETE: Object.freeze({ label: "Prompt: suppression" }),
  PROMPT_UPLOAD_FILE: Object.freeze({ label: "Prompt: upload de fichier" }),

  AUDIO_PODCAST_SEARCH: Object.freeze({ label: "Audio & Podcast: recherche" }),
  AUDIO_PODCAST_TAG_ADD: Object.freeze({ label: "Audio & Podcast: ajout d'un tag IPTC" }),
  AUDIO_PODCAST_ENTITY_ADD: Object.freeze({ label: "Audio & Podcast: ajout d'une entité" }),
  AUDIO_PODCAST_TAG_FEEDBACK: Object.freeze({ label: "Audio & Podcast: feedback d'un tag IPTC" }),
  AUDIO_PODCAST_ENTITY_FEEDBACK: Object.freeze({ label: "Audio & Podcast: feedback d'une entité" }),
  AUDIO_PODCAST_GET: Object.freeze({ label: "Audio & Podcast: affichage" }),
  AUDIO_PODCAST_CLASSIFY: Object.freeze({ label: "Audio & Podcast: analyse" }),
  AUDIO_PODCAST_SUMMARY_SET: Object.freeze({ label: "Audio & Podcast: génération d'un résumé" }),
  AUDIO_PODCAST_SPEAKER_SET: Object.freeze({ label: "Audio & Podcast: changement de speaker" }),
  AUDIO_PODCAST_SPEAKER_DELETE: Object.freeze({ label: "Audio & Podcast: suppression de speaker" }),
  AUDIO_PODCAST_TAIL_SET: Object.freeze({ label: "Audio & Podcast: changement du tail" }),
  AUDIO_PODCAST_TEXT_EDIT: Object.freeze({ label: "Audio & Podcast: édition du texte" }),
  AUDIO_PODCAST_USERNEED_SET: Object.freeze({ label: "Audio & Podcast: changement de userneed" }),
  AUDIO_PODCAST_TRANSLATE: Object.freeze({ label: "Audio & Podcast: traduction" }),
  AUDIO_PODCAST_SUBTITLES_GENERATE: Object.freeze({ label: "Audio & Podcast: génération de sous-titres" }),
  AUDIO_PODCAST_STATS_USERNEED: Object.freeze({ label: "Audio & Podcast: statistiques des userneeds" }),
  AUDIO_PODCAST_STATS_ENTITY: Object.freeze({ label: "Audio & Podcast: statistiques des entités" }),

  PLAY_RTS_SEARCH: Object.freeze({ label: "Play RTS: recherche" }),
  PLAY_RTS_TAG_ADD: Object.freeze({ label: "Play RTS: ajout d'un tag IPTC" }),
  PLAY_RTS_ENTITY_ADD: Object.freeze({ label: "Play RTS: ajout d'une entité" }),
  PLAY_RTS_TAG_FEEDBACK: Object.freeze({ label: "Play RTS: feedback d'un tag IPTC" }),
  PLAY_RTS_ENTITY_FEEDBACK: Object.freeze({ label: "Play RTS: feedback d'une entité" }),
  PLAY_RTS_GET: Object.freeze({ label: "Play RTS: affichage" }),
  PLAY_RTS_CLASSIFY: Object.freeze({ label: "Play RTS: analyse" }),
  PLAY_RTS_SUMMARY_SET: Object.freeze({ label: "Play RTS: génération d'un résumé" }),
  PLAY_RTS_SPEAKER_SET: Object.freeze({ label: "Play RTS: changement de speaker" }),
  PLAY_RTS_SPEAKER_DELETE: Object.freeze({ label: "Play RTS: suppression de speaker" }),
  PLAY_RTS_TAIL_SET: Object.freeze({ label: "Play RTS: changement du tail" }),
  PLAY_RTS_TEXT_EDIT: Object.freeze({ label: "Play RTS: édition du texte" }),
  PLAY_RTS_USERNEED_SET: Object.freeze({ label: "Play RTS: changement de userneed" }),
  PLAY_RTS_TRANSLATE: Object.freeze({ label: "Play RTS: traduction" }),
  PLAY_RTS_SUBTITLES_GENERATE: Object.freeze({ label: "Play RTS: génération de sous-titres" }),
  PLAY_RTS_STATS_USERNEED: Object.freeze({ label: "Play RTS: statistiques des userneeds" }),
  PLAY_RTS_STATS_ENTITY: Object.freeze({ label: "Play RTS: statistiques des entités" }),

  AUDIOVISUAL_MEDIA_SEARCH: Object.freeze({ label: "Audiovisual Media: recherche" }),
  AUDIOVISUAL_MEDIA_TAG_ADD: Object.freeze({ label: "Audiovisual Media: ajout d'un tag IPTC" }),
  AUDIOVISUAL_MEDIA_ENTITY_ADD: Object.freeze({ label: "Audiovisual Media: ajout d'une entité" }),
  AUDIOVISUAL_MEDIA_TAG_FEEDBACK: Object.freeze({ label: "Audiovisual Media: feedback d'un tag IPTC" }),
  AUDIOVISUAL_MEDIA_ENTITY_FEEDBACK: Object.freeze({ label: "Audiovisual Media: feedback d'une entité" }),
  AUDIOVISUAL_MEDIA_GET: Object.freeze({ label: "Audiovisual Media: affichage" }),
  AUDIOVISUAL_MEDIA_SUMMARY_SET: Object.freeze({ label: "Audiovisual Media: génération d'un résumé" }),
  AUDIOVISUAL_MEDIA_SPEAKER_SET: Object.freeze({ label: "Audiovisual Media: changement de speaker" }),
  AUDIOVISUAL_MEDIA_SPEAKER_DELETE: Object.freeze({ label: "Audiovisual Media: suppression de speaker" }),
  AUDIOVISUAL_MEDIA_TAIL_SET: Object.freeze({ label: "Audiovisual Media: changement du tail" }),
  AUDIOVISUAL_MEDIA_TEXT_EDIT: Object.freeze({ label: "Audiovisual Media: édition du texte" }),
  AUDIOVISUAL_MEDIA_USERNEED_SET: Object.freeze({ label: "Audiovisual Media: changement de userneed" }),
  AUDIOVISUAL_MEDIA_TRANSLATE: Object.freeze({ label: "Audiovisual Media: traduction" }),
  AUDIOVISUAL_MEDIA_SUBTITLES_GENERATE: Object.freeze({ label: "Audiovisual Media: génération de sous-titres" }),
  AUDIOVISUAL_MEDIA_STATS_USERNEED: Object.freeze({ label: "Audiovisual Media: statistiques des userneeds" }),
  AUDIOVISUAL_MEDIA_STATS_ENTITY: Object.freeze({ label: "Audiovisual Media: statistiques des entités" }),
  API_AUDIOVISUAL_MEDIA_ANALYSIS: Object.freeze({ label: "API Audiovisual Media: import et analyse" }),

  USER_PROFILE_EDIT: Object.freeze({ label: "Profil utilisateur: édition" }),
  HOMEPAGE_FEATURE_DEMO_PLAY: Object.freeze({ label: "Page d'accueil: lecture d'une démo" }),
  TOPBAR_ASSISTANT_HELP: Object.freeze({ label: "Barre de menu: assistant d'aide" }),

  SUBTITLES_TRANSLATE: Object.freeze({ label: "Sous-titres: traduction" }),

  VERTEXAI_STREAM: Object.freeze({ label: "Vertex AI: streaming" }),

  SEARCH: Object.freeze({ label: "Beta test: recherche globale" }),

  API_ARTICLE_ANALYSIS: Object.freeze({ label: "API Article: analyse" }),

  API_ENTITY_GET_ID: Object.freeze({ label: "API Entité: recherche par ID" }),
  API_ENTITY_GET_WIKIDATAID: Object.freeze({ label: "API Entité: recherche par wikidata ID" }),
  API_ENTITY_SEARCH: Object.freeze({ label: "API Entité: recherche d'une page par critères" }),
  API_ENTITY_CREATE_WIKIDATAID: Object.freeze({ label: "API Entité: création par wikidata ID" }),
  API_ENTITY_CREATE_LABEL: Object.freeze({ label: "API Entité: création par label" }),

  API_PROMPT_DOPESHEET: Object.freeze({ label: "API Prompts: prompt pour un dopesheet" }),
  API_PROMPT_BY_ID: Object.freeze({ label: "API Prompts: prompt par ID" }),
  API_PROMPT_CUSTOM: Object.freeze({ label: "API Prompts: prompt custom" }),
});
